/**
 * components/BatchProcessingView.jsx
 *
 * Parallel batch processing — all files run simultaneously.
 *
 * Layout:
 *   ┌─ File selector strip ────────────────────────────────────┐
 *   │  3 files · 1 completed · 2 processing  [Cancel All]      │
 *   │  [● drawing-A.pdf]  [✓ drawing-B.pdf]  [● drawing-C.pdf] │
 *   └──────────────────────────────────────────────────────────┘
 *   ┌─ Focused ProcessStatus-style card ───────────────────────┐
 *   │  full step list + progress bar + completion banner        │
 *   └──────────────────────────────────────────────────────────┘
 *
 * All FileProgressCard components stay mounted so their polling
 * loops continue for every file simultaneously.  Only the focused
 * card is visible; others are hidden via display:none.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import API from '../services/api';
import '../styles/components.css';

/* ── Time-based progress interpolation (mirrors ProcessStatus.jsx) ── */
const KF = [[0,3],[0.08,15],[0.22,28],[0.40,42],[0.58,55],[0.74,67],[0.88,78],[1,90]];
const ASSUMED_MS = 300_000; // 5 min — covers worst-case 10-file parallel Gemini batch
const STALE_TIMEOUT_MS = 1_500_000; // 25 min — accommodates 10 sequential files at ~2 min each
const interpProgress = (t) => {
  const ct = Math.min(1, Math.max(0, t));
  for (let i = 1; i < KF.length; i++) {
    if (ct <= KF[i][0]) {
      const r = (ct - KF[i-1][0]) / (KF[i][0] - KF[i-1][0]);
      return KF[i-1][1] + r * (KF[i][1] - KF[i-1][1]);
    }
  }
  return 90;
};

/* ── Per-step cycling detail messages ────────────────────────── */
/* ── Animated blueprint scanner — the visual heart of the processing view ── */
function BlueprintScanner({ running, failed, done }) {
  const beam = running && !failed && !done;
  return (
    <div className={`scn-stage ${failed ? 'failed' : done ? 'done' : ''}`}>
      <svg viewBox="0 0 260 300" className="scn-svg">
        {/* sheet */}
        <rect x="10" y="10" width="240" height="280" rx="6" className="scn-sheet" />
        {/* border zone ticks */}
        {[50, 90, 130, 170, 210].map(x => <line key={'t'+x} x1={x} y1="10" x2={x} y2="16" className="scn-tick" />)}
        {[60, 110, 160, 210, 250].map(y => <line key={'l'+y} x1="10" y1={y} x2="16" y2={y} className="scn-tick" />)}
        {/* part geometry */}
        <rect x="55" y="60" width="150" height="120" rx="10" className="scn-part" />
        <circle cx="90"  cy="95"  r="11" className="scn-hole h1" />
        <circle cx="170" cy="95"  r="11" className="scn-hole h2" />
        <circle cx="90"  cy="150" r="11" className="scn-hole h3" />
        <circle cx="170" cy="150" r="11" className="scn-hole h4" />
        <circle cx="130" cy="122" r="18" className="scn-hole big" />
        {/* dimension lines */}
        <line x1="55" y1="200" x2="205" y2="200" className="scn-dim" />
        <line x1="55" y1="195" x2="55" y2="205" className="scn-dim" />
        <line x1="205" y1="195" x2="205" y2="205" className="scn-dim" />
        <line x1="225" y1="60" x2="225" y2="180" className="scn-dim" />
        {/* callout pings — light up as the beam passes */}
        <circle cx="90"  cy="95"  r="16" className="scn-ping p1" />
        <circle cx="170" cy="150" r="16" className="scn-ping p2" />
        <circle cx="130" cy="122" r="24" className="scn-ping p3" />
        <circle cx="130" cy="200" r="14" className="scn-ping p4" />
        {/* found labels */}
        <text x="196" y="216" className="scn-found f1">150.0</text>
        <text x="60"  y="52"  className="scn-found f2">Ø 36.0</text>
        <text x="180" y="86"  className="scn-found f3">4X Ø 11</text>
        {/* scanning beam */}
        {beam && (
          <g className="scn-beam-g">
            <rect x="10" y="0" width="240" height="26" className="scn-beam-glow" />
            <line x1="10" y1="26" x2="250" y2="26" className="scn-beam" />
          </g>
        )}
        {done && <text x="130" y="160" textAnchor="middle" className="scn-done-mark">✓</text>}
        {failed && <text x="130" y="160" textAnchor="middle" className="scn-fail-mark">✕</text>}
      </svg>
      <div className="scn-stage-caption">
        {failed ? 'Scan interrupted' : done ? 'Every zone accounted for' : 'Scanning the sheet, zone by zone'}
      </div>
    </div>
  );
}

function ProgressRing({ pct, failed, done }) {
  const R = 30, C = 2 * Math.PI * R;
  const off = C * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return (
    <svg viewBox="0 0 76 76" className="scn-ring">
      <circle cx="38" cy="38" r={R} className="scn-ring-track" />
      <circle
        cx="38" cy="38" r={R}
        className={`scn-ring-bar ${failed ? 'failed' : done ? 'done' : ''}`}
        strokeDasharray={C}
        strokeDashoffset={off}
      />
      <text x="38" y="43" textAnchor="middle" className="scn-ring-text">
        {Math.round(pct)}%
      </text>
    </svg>
  );
}

const STEP_DETAILS = [
  [ // PREPROCESS
    'Loading your drawing file…',
    'Cleaning up the image quality…',
    'Adjusting brightness and contrast…',
    'Removing background noise…',
    'Sharpening edges and text…',
  ],
  [ // OCR
    'Sweeping the sheet zone by zone, like an inspector at a light table…',
    'Reading diameter callouts — Ø values and their tolerances…',
    'Found linear dimensions — capturing nominal and limits…',
    'Reading radii, chamfers and angular callouts…',
    'Checking section views and detail views for hidden callouts…',
    'Reading thread callouts and insert specifications…',
    'Collecting every measurable feature on the sheet…',
  ],
  [ // CC
    'Hunting for critical characteristic symbols across every view…',
    'Found GD&T feature control frames — reading tolerance zones…',
    'Reading datum references — A, B, C alignment scheme…',
    'Linking each CC symbol to its dimension callout…',
    'Separating boxed BASIC dimensions from toleranced ones…',
    'Setting reference dimensions ( ) aside — not directly inspected…',
    'Double-checking ambiguous callouts on zoomed crops…',
  ],
  [ // PLAN
    'Building one inspection row per measurable feature…',
    'Computing UCL and LCL from each tolerance…',
    'Applying block tolerances where no explicit tolerance is drawn…',
    'Linking position tolerances to their hole patterns…',
    'Reading the drawing notes — material, finish, threads…',
    'Flagging uncertain finds for the review table…',
  ],
  [ // RULES
    'Consulting the quality knowledge base for each feature type…',
    'Selecting gauges — calipers, radius gauges, thread gauges…',
    'Applying your business rules to each dimension class…',
    'Grounding every row in inspection best practice…',
    'Verifying nothing on the sheet was left unaccounted…',
  ],
  [ // EXCEL
    'Formatting inspection data…',
    'Applying report layout and styling…',
    'Writing all inspection items…',
    'Adding drawing references…',
    'Saving your report file…',
  ],
];

/* ── Step definitions — mirror ProcessStatus.jsx STEP_DEFS exactly ── */
const STEPS = [
  {
    code:      'PREPROCESS',
    label:     'Preparing Your Drawing',
    desc:      'Getting your drawing ready for analysis',
    threshold: 10,
    doneMsg:   'Drawing is ready for analysis.',
  },
  {
    code:      'OCR',
    label:     'Reading Text & Dimensions',
    desc:      'Extracting all numbers, dimensions and annotations',
    threshold: 25,
    doneMsg:   'Text and dimensions captured.',
  },
  {
    code:      'CC',
    label:     'Finding Critical Features',
    desc:      'Locating all critical inspection points on the drawing',
    threshold: 45,
    doneMsg:   'Critical features identified.',
  },
  {
    code:      'PLAN',
    label:     'Building Your Inspection Plan',
    desc:      'Creating a structured inspection plan for every feature',
    threshold: 65,
    doneMsg:   'Inspection plan structure complete.',
  },
  {
    code:      'RULES',
    label:     'Applying Quality Standards',
    desc:      'Matching each feature to the right inspection tool and method',
    threshold: 82,
    doneMsg:   'Quality standards applied.',
  },
  {
    code:      'EXCEL',
    label:     'Preparing Your Report',
    desc:      'Generating your formatted inspection plan spreadsheet',
    threshold: 100,
    doneMsg:   'Report ready to download.',
  },
];

const MULTI_PART_STEP_DETAILS = [
  'Reading the BOM / variant table…',
  'Identifying part numbers and symbols…',
  'Mapping table rows to drawing views…',
  'Extracting multi-part family data…',
  'Linking table entries to dimensions…',
];

/* Tabular scans insert a multi-part table step after OCR (step 3). */
const TABULAR_STEPS = [
  STEPS[0],
  STEPS[1],
  {
    code:      'MULTI_PART',
    label:     'Analysing Multi-Part Table',
    desc:      'Reading the BOM table and mapping each part to the drawing',
    threshold: 38,
    doneMsg:   'Multi-part table analysed.',
  },
  ...STEPS.slice(2).map((step, i) => {
    const thresholds = [50, 64, 79, 100];
    return { ...step, threshold: thresholds[i] ?? step.threshold };
  }),
];

const TABULAR_STEP_DETAILS = [
  STEP_DETAILS[0],
  STEP_DETAILS[1],
  MULTI_PART_STEP_DETAILS,
  STEP_DETAILS[2],
  STEP_DETAILS[3],
  STEP_DETAILS[4],
  STEP_DETAILS[5],
];

const MANUAL_MINUTES = 120; // baseline manual time (~2 hrs for a QE)

/* ── Time helpers ────────────────────────────────────────────── */
function formatElapsed(ms) {
  if (!ms) return '—';
  const s = Math.max(1, Math.round(ms / 1000) - 25);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function formatTimeSaved(ms) {
  if (!ms) return '—';
  const elapsed    = Math.max(1000, ms - 25000);
  const elapsedMin = elapsed / 60000;
  const saved      = Math.max(1, MANUAL_MINUTES - elapsedMin);
  if (saved >= 60) {
    const h = Math.floor(saved / 60);
    const m = Math.round(saved % 60);
    return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
  }
  return `~${Math.round(saved)}m`;
}

/** Derive each step's status from progress and card status. */
function getStepStatuses(progress, cardStatus, steps) {
  if (cardStatus === 'completed') return steps.map(() => 'completed');
  const activeIdx = steps.findIndex(s => progress <= s.threshold);
  const ai = activeIdx === -1 ? steps.length - 1 : activeIdx;
  return steps.map((_, i) => {
    if (cardStatus === 'failed' && i === ai) return 'error';
    if (i < ai)   return 'completed';
    if (i === ai) return cardStatus === 'processing' ? 'running' : 'pending';
    return 'pending';
  });
}

/** Friendly live-detail for the currently active step, cycling through messages. */
function activeLiveDetail(progress, liveDetailIdx, steps, stepDetails) {
  const stepIdx = steps.findIndex(s => progress <= s.threshold);
  const ai      = stepIdx === -1 ? steps.length - 1 : stepIdx;
  const details = stepDetails[ai];
  return details[liveDetailIdx % details.length];
}

/* ── SVG icons ───────────────────────────────────────────────── */
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

/* ══ File selector strip ══════════════════════════════════════ */
function FileSelectorStrip({ queue, cardStatuses, focusedIdx, onSelect, runningCount, doneCount, failedCount, onCancelAll, allDone, allResultsReady, onViewResults }) {
  const total = queue.length;

  return (
    <div className="fps-strip">
      {/* Summary row */}
      <div className="fps-summary">
        <div className="fps-summary-counts">
          <span className="fps-total">{total} file{total !== 1 ? 's' : ''} uploaded</span>
          {runningCount > 0 && (
            <span className="fps-count processing">
              <span className="fps-dot processing" />
              {runningCount} processing
            </span>
          )}
          {doneCount > 0 && (
            <span className="fps-count completed">
              <span className="fps-dot completed">✓</span>
              {doneCount} completed
            </span>
          )}
          {failedCount > 0 && (
            <span className="fps-count failed">
              <span className="fps-dot failed">✕</span>
              {failedCount} failed
            </span>
          )}
        </div>
        {runningCount > 0 && (
          <button className="fps-cancel-all" onClick={onCancelAll}>Cancel All</button>
        )}
      </div>

      {/* File pills row */}
      {total > 1 && (
        <div className="fps-pills">
          {queue.map((item, i) => {
            const st      = cardStatuses[item.fileId] || 'processing';
            const isActive = i === focusedIdx;
            return (
              <button
                key={item.fileId}
                className={`fps-pill ${st} ${isActive ? 'active' : ''}`}
                onClick={() => onSelect(i)}
                title={
                  (st === 'failed' || st === 'cancelled') && item.error
                    ? item.error
                    : item.fileName
                }
              >
                <span className={`fps-pill-dot ${st}`}>
                  {st === 'completed' && '✓'}
                  {(st === 'failed' || st === 'cancelled') && '✕'}
                </span>
                <span className="fps-pill-name">{item.fileName}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Fetching indicator — pipelines done but results fetch still in progress */}
      {allDone && !allResultsReady && (
        <div className="fps-finalizing">
          <span className="fps-finalizing-spinner" />
          Fetching results…
        </div>
      )}

      {/* View Results button — only shown once all result payloads are loaded */}
      {allDone && allResultsReady && (
        <button className="fps-view-results-btn" onClick={onViewResults}>
          View Results →
        </button>
      )}
    </div>
  );
}

/* ══ Single file progress card ════════════════════════════════ */
/** Optional tiny stagger (ms) to soften simultaneous POST /process — default 0; cap keeps total delay under ~100ms */
function FileProgressCard({ item, apiKey, onComplete, onError, onStatusChange, processStartDelayMs = 0 }) {
  const { fileId, fileName, ccSymbols, ccSymbol } = item;
  const isTabular = item.scanType === 'tabular';
  const steps = isTabular ? TABULAR_STEPS : STEPS;
  const stepDetails = isTabular ? TABULAR_STEP_DETAILS : STEP_DETAILS;

  const [progress,      setProgress]      = useState(3);
  const [cardStatus,    setCardStatus]    = useState('processing');
  const [elapsedMs,     setElapsedMs]     = useState(0);
  const [cancelling,    setCancelling]    = useState(false);
  const [barState,      setBarState]      = useState('animating');
  const [liveDetailIdx, setLiveDetailIdx] = useState(0);
  const [failureDetail, setFailureDetail] = useState(null);

  const startTimeRef    = useRef(Date.now());
  const pollRef         = useRef(null);
  const timerRef        = useRef(null);
  const tickerRef       = useRef(null);
  const detailTickerRef = useRef(null);
  const doneRef         = useRef(false);
  const statusRef       = useRef('processing');
  const progressHigh    = useRef(3);

  const finish = useCallback((finalStatus, msg) => {
    if (doneRef.current) return;
    doneRef.current   = true;
    statusRef.current = finalStatus;
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
    clearInterval(tickerRef.current);
    clearInterval(detailTickerRef.current);

    const finalMs = Date.now() - startTimeRef.current;
    setElapsedMs(finalMs);
    setCardStatus(finalStatus);
    onStatusChange(fileId, finalStatus);

    if (finalStatus === 'completed') {
      setBarState('complete');
      const startPct = progressHigh.current;
      const t0 = Date.now();
      const dur = 1200;
      const anim = setInterval(() => {
        const t   = Math.min(1, (Date.now() - t0) / dur);
        const cur = Math.round(startPct + (100 - startPct) * t);
        progressHigh.current = cur;
        setProgress(cur);
        if (cur >= 100) clearInterval(anim);
      }, 30);
      onComplete(fileId);
    } else {
      setBarState('failed');
      setProgress(progressHigh.current);
      if (msg && finalStatus !== 'cancelled') setFailureDetail(msg);
      onError(fileId, msg || 'Processing failed');
    }
  }, [fileId, onComplete, onError, onStatusChange]);

  useEffect(() => {
    onStatusChange(fileId, 'processing');

    // Elapsed-time counter — updates every second
    timerRef.current = setInterval(() => {
      if (statusRef.current !== 'processing') { clearInterval(timerRef.current); return; }
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 1000);

    // Time-based progress ticker — starts IMMEDIATELY so bar moves from the first frame,
    // even while API.process() is still awaiting a response (can take several seconds
    // when 10 files are starting concurrently).
    tickerRef.current = setInterval(() => {
      if (statusRef.current !== 'processing') { clearInterval(tickerRef.current); return; }
      const elapsed   = Date.now() - startTimeRef.current;
      const timePct   = interpProgress(elapsed / ASSUMED_MS);
      const serverPct = progressHigh.current;
      setProgress(p => Math.max(p, Math.min(89, Math.max(timePct, serverPct))));
    }, 200);

    // Cycling step detail messages — starts immediately so text changes from the start
    detailTickerRef.current = setInterval(() => {
      if (statusRef.current !== 'processing') { clearInterval(detailTickerRef.current); return; }
      setLiveDetailIdx(idx => idx + 1);
    }, 2500);

    let mounted = true;

    const poll = async () => {
      if (!mounted || statusRef.current !== 'processing') return;

      // Safety net: if stuck in processing for too long (backend crash / restart),
      // stop spinning and show a recoverable error so the user knows to retry.
      if (Date.now() - startTimeRef.current > STALE_TIMEOUT_MS) {
        if (mounted) finish('failed', 'Processing timed out. The server may have restarted — please re-upload and try again.');
        return;
      }

      try {
        const resp = await API.getProcessStatus(fileId);
        if (!mounted) return;
        if (resp.progress !== undefined) {
          progressHigh.current = Math.max(progressHigh.current, resp.progress);
        }
        if (resp.status === 'completed') {
          finish('completed');
        } else if (resp.status === 'failed') {
          finish('failed', resp.error_message || 'Processing failed');
        }
      } catch { /* transient — keep polling */ }
    };

    const PROCESS_START_ATTEMPTS = 2;
    const run = async () => {
      try {
        const current = await API.getProcessStatus(fileId);
        if (!mounted) return;
        if (current.status === 'completed') {
          finish('completed');
          return;
        }
        if (current.status === 'processing') {
          pollRef.current = setInterval(poll, 4000);
          return;
        }
      } catch {
        /* status check failed — attempt to start below */
      }

      let lastErr = null;
      for (let attempt = 0; attempt < PROCESS_START_ATTEMPTS; attempt++) {
        try {
          if (item.scanType === 'tabular') {
            await API.startTabularScan(fileId, apiKey, ccSymbols || (ccSymbol ? [ccSymbol] : []));
          } else {
            await API.process(fileId, apiKey, ccSymbols || (ccSymbol ? [ccSymbol] : ['spade']));
          }
          lastErr = null;
          break;
        } catch (err) {
          if (err?.httpStatus === 409) {
            finish('completed');
            return;
          }
          lastErr = err;
          if (attempt < PROCESS_START_ATTEMPTS - 1) {
            const statusResp = await API.getProcessStatus(fileId).catch(() => null);
            if (statusResp?.status === 'processing') {
              pollRef.current = setInterval(poll, 4000);
              return;
            }
            if (statusResp?.status === 'completed') {
              finish('completed');
              return;
            }
            await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
            continue;
          }
          if (mounted) finish('failed', err.error || 'Failed to start pipeline');
          return;
        }
      }
      if (lastErr != null) return;
      if (!mounted) return;
      pollRef.current = setInterval(poll, 4000);
    };

    let startTimer = null;
    if (processStartDelayMs > 0) {
      startTimer = setTimeout(run, processStartDelayMs);
    } else {
      run();
    }

    return () => {
      mounted = false;
      if (startTimer) clearTimeout(startTimer);
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
      clearInterval(tickerRef.current);
      clearInterval(detailTickerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = async () => {
    if (cancelling || statusRef.current !== 'processing') return;
    const ok = window.confirm(`Cancel processing of "${fileName}"?`);
    if (!ok) return;
    setCancelling(true);
    try { await API.cancelProcessing(fileId); } catch {}
    finish('cancelled', 'Cancelled by user');
  };

  const isProcessing = cardStatus === 'processing';
  const isCompleted  = cardStatus === 'completed';
  const isFailed     = cardStatus === 'failed' || cardStatus === 'cancelled';
  const stepStatuses = getStepStatuses(progress, cardStatus, steps);
  const liveDetail   = activeLiveDetail(progress, liveDetailIdx, steps, stepDetails);
  const activeIdx    = Math.max(0, stepStatuses.indexOf('running') !== -1
    ? stepStatuses.indexOf('running')
    : Math.min(stepStatuses.filter(s => s === 'completed').length, steps.length - 1));

  // Console feed — a scrolling log of narrative lines (newest at the bottom)
  const [feed, setFeed] = useState([]);
  const [feedOpen, setFeedOpen] = useState(false);
  useEffect(() => {
    if (!liveDetail || !isProcessing) return;
    setFeed((prev) => {
      if (prev.length && prev[prev.length - 1].msg === liveDetail) return prev;
      return [...prev.slice(-4), { t: Date.now(), msg: liveDetail }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDetail, isProcessing]);
  const doneSteps    = stepStatuses.filter(s => s === 'completed').length;

  return (
    <div className={`fpc ${cardStatus}`}>

      {/* ── Header ── */}
      <div className="ps-header" style={{ borderRadius: 0 }}>
        <div className="ps-header-left">
          {isProcessing && <span className="ps-live-dot" />}
          {isCompleted  && <span className="ps-done-dot" />}
          {isFailed     && <span className="ps-fail-dot" />}
          <div>
            <h2 className="ps-title">
              {isProcessing
                ? (isTabular ? 'Analysing assembly drawing…' : 'Analysing your drawing…')
               : isCompleted ? 'Analysis complete ✓'
               : 'Analysis failed'}
            </h2>
            <p className="ps-subtitle" style={{ wordBreak: 'break-all' }}>{fileName}</p>
            {isFailed && failureDetail && (
              <p className="ps-error-detail" title={failureDetail} style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-danger, #c0392b)', wordBreak: 'break-word' }}>
                {failureDetail.length > 280 ? `${failureDetail.slice(0, 280)}…` : failureDetail}
              </p>
            )}
          </div>
        </div>
        <div className="ps-header-right">
          <span className={`ps-status-chip ${isProcessing ? 'processing' : isCompleted ? 'completed' : 'failed'}`}>
            {isCompleted ? '100%' : `${Math.round(progress)}%`}
          </span>
          <span className="ps-step-count">{doneSteps} of {steps.length} steps done</span>
          {isProcessing && (
            <button className="ps-cancel-btn" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling…' : '✕ Cancel'}
            </button>
          )}
        </div>
      </div>

      {/* ── Progress bar ── */}
      <div className="ps-progress-track">
        <div className={`ps-progress-bar ${barState}`} style={{ width: `${progress}%` }} />
      </div>

      {/* ── Step flow with embedded live extraction console ── */}
      <div className="ps-steps-list">
        {steps.map((step, i) => {
          const st        = stepStatuses[i];
          const isRunning = st === 'running';
          const isDone    = st === 'completed';
          const isError   = st === 'error';
          const isPending = st === 'pending';

          return (
            <div key={step.code} className={`ps-step-row ${st}`}>
              <div className="ps-step-left">
                <div className={`ps-step-node ${st}`}>
                  {isDone    && <CheckIcon />}
                  {isError   && <XIcon />}
                  {isRunning && <span className="ps-spin-ring" />}
                  {isPending && <span className="ps-step-num">{i + 1}</span>}
                </div>
                {i < steps.length - 1 && (
                  <div className={`ps-vert-connector ${isDone ? 'filled' : ''}`} />
                )}
              </div>

              <div className="ps-step-content">
                <div className="ps-step-head">
                  <div className="ps-step-title-row">
                    <span className="ps-step-name">{step.label}</span>
                    {isDone    && <span className="ps-step-pill done">Done</span>}
                    {isRunning && <span className="ps-step-pill running">In Progress</span>}
                    {isError   && <span className="ps-step-pill error">Failed</span>}
                    {isPending && <span className="ps-step-pill pending">Waiting</span>}
                  </div>
                  <p className="ps-step-desc-text">{step.desc}</p>
                </div>

                {isRunning && (
                  <div className="ps-step-live">
                    <span className="ps-step-live-arrow">→</span>
                    <span className="ps-step-live-msg">{liveDetail}</span>
                    <span className="ps-blink-cursor" />
                  </div>
                )}

                {isDone && (
                  <div className="ps-step-done-line">
                    <span className="ps-step-done-check">✓</span>
                    <span>{step.doneMsg}</span>
                  </div>
                )}

                {isError && (
                  <div className="ps-step-error-line">
                    {failureDetail
                      ? failureDetail.length > 200
                        ? `${failureDetail.slice(0, 200)}…`
                        : failureDetail
                      : 'An issue occurred at this step. Please try uploading again.'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Live Feed — collapsible log below the step flow ── */}
      {isProcessing && (
        <div className="scn-feed-acc">
          <button className="scn-feed-toggle" onClick={() => setFeedOpen(v => !v)}>
            <span className="scn-feed-dot" />
            <span>Live Feed</span>
            <span className="scn-feed-hint">{feedOpen ? 'hide activity log' : 'show activity log'}</span>
            <span className="scn-feed-caret">{feedOpen ? '▴' : '▾'}</span>
          </button>
          {feedOpen && (
            <div className="scn-console inline">
              <div className="scn-console-bar">
                <span className="scn-led" /><span className="scn-led amber" /><span className="scn-led green" />
                <span className="scn-console-title">live extraction feed</span>
              </div>
              <div className="scn-console-body">
                {feed.length === 0 && (
                  <div className="scn-console-line current">▸ Initialising analysis engine…</div>
                )}
                {feed.map((l, idx) => (
                  <div key={l.t} className={`scn-console-line ${idx === feed.length - 1 ? 'current' : ''}`}>
                    ▸ {l.msg}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Completion banner with time stats ── */}
      {isCompleted && (
        <div className="ps-completion-banner" style={{ borderRadius: 0 }}>
          <div className="ps-completion-top">
            <span className="ps-completion-icon">✓</span>
            <div className="ps-completion-text">
              <strong>All {steps.length} steps completed.</strong>
              <span>{isTabular ? ' Your tabular inspection plan is ready.' : ' Your inspection plan is ready.'}</span>
            </div>
          </div>
          <div className="ps-time-stats">
            <div className="ps-time-stat ai">
              <span className="ps-time-stat-icon">⚡</span>
              <div>
                <div className="ps-time-stat-value">{formatElapsed(elapsedMs)}</div>
                <div className="ps-time-stat-label">AI completed in</div>
              </div>
            </div>
            <div className="ps-time-stat-divider" />
            <div className="ps-time-stat manual">
              <span className="ps-time-stat-icon">🕐</span>
              <div>
                <div className="ps-time-stat-value">~{MANUAL_MINUTES / 60}h</div>
                <div className="ps-time-stat-label">Typical manual time</div>
              </div>
            </div>
            <div className="ps-time-stat-divider" />
            <div className="ps-time-stat saved">
              <span className="ps-time-stat-icon">🚀</span>
              <div>
                <div className="ps-time-stat-value">{formatTimeSaved(elapsedMs)}</div>
                <div className="ps-time-stat-label">Time saved</div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* ══ Batch container ══════════════════════════════════════════ */
export default function BatchProcessingView({ queue, apiKey, onComplete, onError, onCancelAll, onViewResults, allResultsReady }) {
  const [cardStatuses, setCardStatuses] = useState(() =>
    Object.fromEntries(queue.map(i => [i.fileId, 'processing']))
  );
  const [focusedIdx, setFocusedIdx] = useState(0);

  const handleStatusChange = useCallback((fileId, status) => {
    setCardStatuses(prev => ({ ...prev, [fileId]: status }));
  }, []);

  // Auto-advance: when the focused file settles, switch to the next processing one
  useEffect(() => {
    const focusedId = queue[focusedIdx]?.fileId;
    const st = cardStatuses[focusedId];
    if (st !== 'completed' && st !== 'failed' && st !== 'cancelled') return;

    const nextRunning = queue.findIndex(
      (item, i) => i !== focusedIdx && cardStatuses[item.fileId] === 'processing'
    );
    if (nextRunning !== -1) setFocusedIdx(nextRunning);
  }, [cardStatuses, focusedIdx, queue]);

  const statuses     = Object.values(cardStatuses);
  const runningCount = statuses.filter(s => s === 'processing').length;
  const doneCount    = statuses.filter(s => s === 'completed').length;
  const failedCount  = statuses.filter(s => s === 'failed' || s === 'cancelled').length;
  const total        = queue.length;
  const allDone      = total > 0 && runningCount === 0 && (doneCount + failedCount) === total;

  return (
    <div className="bpv-root">

      {/* ── File selector strip ── */}
      <FileSelectorStrip
        queue={queue}
        cardStatuses={cardStatuses}
        focusedIdx={focusedIdx}
        onSelect={setFocusedIdx}
        runningCount={runningCount}
        doneCount={doneCount}
        failedCount={failedCount}
        onCancelAll={onCancelAll}
        allDone={allDone}
        allResultsReady={allResultsReady}
        onViewResults={onViewResults}
      />

      {/* ── Cards: all mounted, only focused is visible ── */}
      {queue.map((item, i) => (
        <div key={item.fileId} style={{ display: i === focusedIdx ? 'block' : 'none' }}>
          <FileProgressCard
            item={item}
            apiKey={apiKey}
            onComplete={onComplete}
            onError={onError}
            onStatusChange={handleStatusChange}
            processStartDelayMs={Math.min(i * 40, 400)}
          />
        </div>
      ))}

      {/* Prominent View Results CTA — only shown once all result payloads are loaded */}
      {allDone && allResultsReady && (
        <div className="bpv-results-cta">
          <p className="bpv-results-cta-label">
            Your inspection plan is ready to review.
          </p>
          <button className="bpv-view-results-btn" onClick={onViewResults}>
            <span className="bpv-view-results-icon">✓</span>
            View Results
            <span className="bpv-view-results-arrow">→</span>
          </button>
        </div>
      )}

    </div>
  );
}
